import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flame, Check, Circle, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { RL } from "./ReadLink";
import { BookPracticeRows } from "./BookPracticeRows";
import "../../styles/GraphPrintz.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;

export { RL };

const fmtDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/* Large brass dial. 270° sweep; scale numerals sit OUTSIDE the arc and the
   value sits in the open bottom gap, so nothing can collide with the needle. */
export function ConsistencyDial({ value, centerLabel, settling = false }) {
  const raw = Math.max(0, Math.min(100, Number(value) || 0));
  const pct = settling ? Math.min(raw, 25) : raw;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setShown(pct), 60);
    return () => window.clearTimeout(t);
  }, [pct]);

  const size = 260, cx = 130, cy = 124, r = 88;
  const START = 135, SWEEP = 270;
  const ang = (p) => START + (p / 100) * SWEEP;
  const pt = (a, rad) => ({ x: cx + rad * Math.cos((a * Math.PI) / 180), y: cy + rad * Math.sin((a * Math.PI) / 180) });
  const arcPath = (p) => {
    const s = pt(START, r), e = pt(ang(p), r);
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${ang(p) - START > 180 ? 1 : 0} 1 ${e.x} ${e.y}`;
  };
  const fill = shown > 0 ? Math.max(shown, 1.5) : 0;

  return (
    <svg className={`gpd-dial${settling ? " is-settling" : ""}`} viewBox={`0 0 ${size} ${size - 14}`} role="img" aria-label={settling ? "Week 1, settling in" : `Consistency ${pct}%`} data-testid="gpd-dial" data-settling={settling}>
      <defs>
        <radialGradient id="gpdFace" cx="50%" cy="45%">
          <stop offset="0%" stopColor="#FCF5E3" />
          <stop offset="100%" stopColor="#EBD6A6" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r + 26} fill="url(#gpdFace)" stroke="#8B6508" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r={r + 21} fill="none" stroke="rgba(139,101,8,0.35)" strokeWidth="1" />
      <path d={arcPath(100)} fill="none" stroke="#E3CC93" strokeWidth="10" strokeLinecap="round" />
      <path
        d={arcPath(100)} pathLength="100" className="gpd-dial-fill" fill="none" stroke={settling ? "#B08A4E" : "#9C5A1E"}
        strokeWidth="10" strokeLinecap="round" strokeDasharray={`${fill} 100`}
        style={{ opacity: fill > 0 ? (settling ? 0.6 : 1) : 0 }} data-testid="gpd-dial-fill"
      />
      {Array.from({ length: 21 }, (_, i) => i * 5).map((t) => {
        const major = t % 25 === 0;
        const a = pt(ang(t), r - 9), b = pt(ang(t), r - (major ? 19 : 14));
        return <line key={t} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#5C4033" strokeWidth={major ? 1.6 : 1} />;
      })}
      {[0, 25, 50, 75, 100].map((t) => {
        const p = pt(ang(t), r + 13);
        return (
          <text key={t} x={p.x} y={p.y} className="gpd-dial-scale" textAnchor="middle" dominantBaseline="middle" data-testid={`gpd-dial-scale-${t}`}>
            {t}
          </text>
        );
      })}
      <g className="gpd-needle" style={{ transform: `rotate(${ang(shown)}deg)`, transformOrigin: `${cx}px ${cy}px` }} data-testid="gpd-dial-needle">
        <polygon points={`${cx - 12},${cy - 3} ${cx + r - 28},${cy} ${cx - 12},${cy + 3}`} fill="#3A1F12" />
      </g>
      <circle cx={cx} cy={cy} r="9" fill="#8B6508" stroke="#3A1F12" strokeWidth="1.2" />
      <circle cx={cx} cy={cy} r="3" fill="#F3E5AB" />
      {centerLabel ? (
        <text x={cx} y={cy + 84} className="gpd-dial-value gpd-dial-value-soft" textAnchor="middle" data-testid="gpd-dial-value">
          {centerLabel}
        </text>
      ) : (
        <text x={cx} y={cy + 86} className="gpd-dial-value" textAnchor="middle" data-testid="gpd-dial-value">
          {pct}<tspan className="gpd-dial-unit">%</tspan>
        </text>
      )}
    </svg>
  );
}

const MONTH = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" });

function Calendar({ days, start }) {
  const first = days[0]?.date, last = days[days.length - 1]?.date;
  const range = first && last ? (MONTH(first) === MONTH(last) ? MONTH(last) : `${MONTH(first).split(" ")[0]} – ${MONTH(last)}`) : "";
  return (
    <div className="gpd-card" data-testid="gpd-calendar">
      <p className="gpd-label">Last 30 days <span className="gpd-label-aside" data-testid="gpd-calendar-month">{range}</span></p>
      <ol className="gpd-cal">
        {days.map((d) => (
          <li
            key={d.date}
            title={`${fmtDate(d.date)} — ${d.kept ? "kept" : d.in_practice ? "missed" : "before you began"}`}
            className={`gpd-cal-cell${d.kept ? " is-kept" : d.in_practice && !d.today ? " is-missed" : ""}${d.in_practice ? "" : " is-before"}${d.today ? " is-today" : ""}${d.date === start ? " is-sealed" : ""}`}
            data-testid={`gpd-cal-${d.date}`}
            data-kept={d.kept}
          >
            {d.date === start ? <span className="gpd-cal-sealed">Sealed</span> : null}
            {Number(d.date.slice(8))}
          </li>
        ))}
      </ol>
      <p className="gpd-meaning">
        <span className="gpd-key is-kept" /> kept <span className="gpd-key" /> missed <span className="gpd-key is-today" /> today
        — a day counts when any practice is marked done.
      </p>
    </div>
  );
}

function Practices({ practices, ro }) {
  const books = [...new Set(practices.map((p) => p.book_title))];
  return (
    <div className="gpd-card gpd-span" data-testid="gpd-practices">
      <p className="gpd-label">Practices</p>
      <p className="gpd-meaning">Days kept toward 30 — and whether your Reward and Obstacle are named for Siddhi.</p>
      {books.map((title) => (
        <div key={title} className="gpd-book-group">
          <p className="gpd-book-title">{title}</p>
          {practices.filter((p) => p.book_title === title).map((p) => (
            <div key={`${p.book_id}-${p.idea_id}-${p.legacy ? "l" : "n"}`} className="gpd-practice" data-testid={`gpd-practice-${p.book_id}-${p.idea_id}`}>
              <span className="gpd-practice-name">{p.title}</span>
              <span className="gpd-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, (p.days_kept / p.target) * 100)}%`, minWidth: p.days_kept > 0 ? 3 : 0 }} />
              </span>
              <span className="gpd-practice-count" data-testid={`gpd-practice-count-${p.book_id}-${p.idea_id}`}>{p.legacy ? `${p.days_kept} of ${p.target} days earlier` : `${p.days_kept} of ${p.target} days`}</span>
              {p.legacy ? (
                <RL ro={ro} to={p.continue_href} className="gpd-ticks" data-testid={`gpd-legacy-continue-${p.book_id}`}>
                  <span>{ro ? "Old system · " : ""}Legacy install · Continue in the new journey →</span>
                  {p.earlier_days > 0 && <span className="gpd-legacy-earlier" data-testid={`gpd-legacy-earlier-${p.book_id}`}>earlier practice (before 30-day window)</span>}
                </RL>
              ) : (
                <RL ro={ro} to={`/sadhana?book=${p.book_id}&focus=${p.phala_named ? "vighna" : "phala"}-${p.idea_id}`} className="gpd-ticks" data-testid={`gpd-practice-reflections-${p.book_id}-${p.idea_id}`}>
                  <span className={p.phala_named ? "is-on" : ""}>{p.phala_named ? <Check size={11} /> : <Circle size={9} />} Phala</span>
                  <span className={p.vighna_named ? "is-on" : ""}>{p.vighna_named ? <Check size={11} /> : <Circle size={9} />} Vighna</span>
                </RL>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const MILESTONES = [3, 7, 14, 21, 30];
function Milestone({ streak }) {
  const next = MILESTONES.find((m) => m > streak) || 30;
  const prev = [...MILESTONES].reverse().find((m) => m <= streak) || 0;
  const pct = streak >= 30 ? 100 : ((streak - prev) / (next - prev)) * 100;
  return (
    <div className="gpd-milestone" data-testid="gpd-streak-milestone">
      <span>{streak >= 30 ? "30-day cycle complete" : `Next milestone: ${next} days`}</span>
      <span className="gpd-milestone-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
    </div>
  );
}

const STAGES = ["bodha", "sankalpa", "sadhana", "siddhi"];
const STAGE_TEXT = {
  bodha: "Bodha — seal your Sankalpa next",
  sankalpa: "Sankalpa sealed — your first day awaits",
  sadhana: "Sadhana in progress",
  siddhi: "Siddhi received",
};

function Journeys({ journeys, ro }) {
  return (
    <div className="gpd-card gpd-span" data-testid="gpd-journeys">
      <p className="gpd-label">Journey</p>
      <p className="gpd-meaning">Where each book stands on the path from understanding to becoming.</p>
      {journeys.map((j) => {
        const at = STAGES.indexOf(j.stage);
        return (
          <RL ro={ro} key={j.book_id} to={j.href} className="gpd-journey" data-testid={`gpd-journey-${j.book_id}`} data-stage={j.stage}>
            <span className="gpd-journey-book">{j.book_title}</span>
            <span className="gpd-journey-track">
              {STAGES.map((s, i) => (
                <span key={s} className={`gpd-stage${i <= at ? " is-lit" : ""}${i === at ? " is-current" : ""}`}>
                  <i aria-hidden="true" /> {s[0].toUpperCase() + s.slice(1)}
                </span>
              ))}
            </span>
            <span className="gpd-journey-status">{j.legacy ? "Legacy install — continue in the new journey" : STAGE_TEXT[j.stage]} <ArrowRight size={12} /></span>
          </RL>
        );
      })}
    </div>
  );
}

function Siddhis({ siddhis, ro }) {
  return (
    <div className="gpd-card gpd-span" data-testid="gpd-siddhis">
      <p className="gpd-label">Siddhis</p>
      <p className="gpd-meaning">The retrospectives distilled from your lived practice.</p>
      {siddhis.length === 0 ? (
        <p className="gpd-empty-line">Your first Siddhi arrives once every practice of a book has its Reward and Obstacle named.</p>
      ) : (
        <ul className="gpd-siddhi-list">
          {siddhis.map((s) => (
            <li key={`${s.book_id}-${s.lang}`}>
              <RL ro={ro} to={`/siddhi?book=${s.book_id}`} data-testid={`gpd-siddhi-${s.book_id}-${s.lang}`}>
                <Sparkles size={13} /> {s.book_title} <em>· {fmtDate(s.generated_at)}</em>
              </RL>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const GraphPrintzDashboard = ({ data: given }) => {
  const { isAuthenticated, authHeaders } = useAuth();
  const [fetched, setData] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || given !== undefined) return;
    axios.get(`${API}/dashboard`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [isAuthenticated, authHeaders, given]);

  const data = given !== undefined ? given : fetched;
  if (!isAuthenticated || !data || data.error) return null;
  return <GraphPrintzView data={data} />;
};

export const GraphPrintzView = ({ data, readOnly: ro = false }) => {
  const { stats, journeys, siddhis, active_books: activeBooks = 0, book_rows: bookRows = [] } = data;
  const allPractices = data.practices;
  const practices = allPractices.filter((p) => !p.legacy);
  const isNew = allPractices.length === 0;
  const firstWeek = stats.available_days > 0 && stats.available_days < 7;
  const bookCount = bookRows.length || new Set(practices.map((p) => p.book_id)).size;
  const summary = [
    `${practices.length} practice${practices.length === 1 ? "" : "s"}`,
    `${stats.total_kept_days} day${stats.total_kept_days === 1 ? "" : "s"} kept`,
    siddhis.length ? `Siddhi received for ${siddhis.length} book${siddhis.length === 1 ? "" : "s"}` : `${bookCount} book${bookCount === 1 ? "" : "s"} in practice`,
  ].join(" · ");

  return (
    <section className="gpd" data-testid="mc-graphprintz">
      <header className="gpd-head">
        <h2 className="gpd-title">GraphPrintz<sup>™</sup></h2>
        <p className="gpd-sub">Am I becoming who I decided to be?</p>
        {!isNew && <p className="gpd-summary" data-testid="gpd-summary">{summary}</p>}
        {!ro && <Link to="/my-journey" className="mj-link" data-testid="gpd-my-journey-link">My Journey — every book in detail <ArrowRight size={12} /></Link>}
      </header>

      {activeBooks >= 5 && (
        <p className="gpd-gentle" data-testid="gpd-many-books-note">
          You&apos;re practising {practices.length} habits — many people keep it to a few at a time.
        </p>
      )}
      {isNew ? (
        <div className="gpd-card gpd-empty" data-testid="gpd-empty">
          <p>Your first day begins when you mark a practice done.</p>
          <RL ro={ro} to="/library" className="gpd-cta" data-testid="gpd-empty-cta">Choose a book <ArrowRight size={13} /></RL>
        </div>
      ) : (
        <div className="gpd-grid">
          <div className="gpd-card gpd-dial-card" data-testid="gpd-consistency">
            <p className="gpd-label">Consistency</p>
            <ConsistencyDial value={stats.consistency_percent} centerLabel={firstWeek ? `Day ${stats.available_days}` : null} settling={firstWeek} />
            {firstWeek && <p className="gpd-settling" data-testid="gpd-dial-settling">Week 1 · settling in</p>}
            <p className="gpd-meaning" data-testid="gpd-consistency-meaning">
              {stats.total_kept_days === 0
                ? "Your first day begins when you mark a practice done."
                : firstWeek
                ? `Day ${stats.available_days} of your first week — ${stats.kept_in_window} kept so far. Consistency settles after day 7.`
                : `You kept ${stats.kept_in_window} of ${stats.available_days} days since sealing (last 30 days).`}
            </p>
            <p className="gpd-caption">A day counts when any of your practices is marked done; per-practice progress is shown below.</p>
          </div>
          <div className="gpd-stack">
            <div className="gpd-card gpd-streak" data-testid="gpd-streak">
              <p className="gpd-label">Streak</p>
              <p className="gpd-streak-num">
                <Flame size={22} /> <span data-testid="gpd-streak-current">{stats.current_streak}</span>
                <small>day{stats.current_streak === 1 ? "" : "s"}</small>
              </p>
              <Milestone streak={stats.current_streak} />
              <p className="gpd-meaning">
                Best <strong data-testid="gpd-streak-best">{stats.best_streak}</strong> ·{" "}
                {stats.current_streak > 0 ? "keep it alive by practising today." : "practise today to light a new streak."}
              </p>
            </div>
            <Calendar days={stats.calendar} start={stats.sealed_start === false ? null : stats.practice_start} />
          </div>
          <BookPracticeRows rows={bookRows} ro={ro} />
          {practices.length > 0 && <Practices practices={practices} ro={ro} />}
        </div>
      )}
      {journeys.length > 0 && <Journeys journeys={journeys} ro={ro} />}
      <Siddhis siddhis={siddhis} ro={ro} />
    </section>
  );
};
